import { Card, CardContent, Box } from '@mui/material'
import type { TPublisherRequest } from '@modules/types'
import { useOne } from '@refinedev/core'
import { PUBLISHER_EXPAND_PRESETS } from '@modules/api'
import { CART_ITEM_TYPE } from '@modules/api-core'
import type { TFormattedRequestData } from '@utils/requestFormatter'
import { RequestCardTitle } from './common/RequestCardTitle'
import { RequestCardActions } from './common/RequestCardActions'
import { PublicationCardInfo } from './publication/PublicationCardInfo'
import { PublicationDetailsExpanded } from './publication/PublicationDetailsExpanded'
import { LyricsCardInfo } from './lyrics/LyricsCardInfo'
import { LyricsDetailsExpanded } from './lyrics/LyricsDetailsExpanded'

interface RequestShowProps {
  request: TPublisherRequest
  formattedData: TFormattedRequestData
  isExpanded: boolean
  onToggleExpand: () => void
  sharingRequestId?: number
}

export const RequestShow = ({
  request,
  formattedData,
  isExpanded,
  onToggleExpand,
  sharingRequestId,
}: RequestShowProps) => {
  const isLyrics = request.clientRequest?.cartItemTypeId === CART_ITEM_TYPE.LYRICS_ONLY

  const { data: expandedRequest, isLoading: isLoadingDetails } = useOne<TPublisherRequest>({
    resource: 'publisher-request',
    id: sharingRequestId,
    dataProviderName: 'share',
    meta: PUBLISHER_EXPAND_PRESETS.SHARE_REQUEST,
    queryOptions: {
      enabled: isExpanded && !!sharingRequestId,
    },
  })

  const expandedData = expandedRequest?.data.clientRequest
  const expandedFiles = expandedRequest?.data.files
  const loading = isExpanded ? isLoadingDetails : false

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease-in-out',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <CardContent
        sx={{
          flexGrow: 1,
          p: 2,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            minHeight: 0,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Summary panel */}
          <Box
            sx={{
              flex: isExpanded ? '0 0 50%' : '0 0 100%',
              transition: 'flex 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box sx={{ flexGrow: 1, minHeight: 0, px: 1 }}>
              <RequestCardTitle
                title={request.clientRequest?.publicationTitle || 'Untitled Publication'}
              />
              {isLyrics ? (
                <LyricsCardInfo
                  formattedData={formattedData}
                  clientRequest={request.clientRequest}
                />
              ) : (
                <PublicationCardInfo formattedData={formattedData} />
              )}
            </Box>
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              <RequestCardActions
                isExpanded={isExpanded}
                loading={loading}
                onToggleExpand={onToggleExpand}
              />
            </Box>
          </Box>

          {/* Details panel */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: isExpanded ? '50%' : '0%',
              transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden',
              opacity: isExpanded ? 1 : 0,
              borderLeft: isExpanded ? '1px solid' : 'none',
              borderColor: 'divider',
              pl: isExpanded ? 2 : 0,
              pr: isExpanded ? 2 : 0,
              pointerEvents: isExpanded ? 'auto' : 'none',
            }}
          >
            <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
              {isLyrics ? (
                <LyricsDetailsExpanded
                  expandedData={expandedData}
                  files={expandedFiles}
                  loading={loading}
                />
              ) : (
                <PublicationDetailsExpanded
                  expandedData={expandedData}
                  files={expandedFiles}
                  loading={loading}
                />
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
